/**
 * IST clock + NSE trading-calendar helpers — extraction #3a of the
 * decomposition. Previously duplicated verbatim in static/index.html and
 * static/paper.html, with the holiday list additionally mirrored in
 * data/trading_calendar.py (three copies to update every calendar year).
 * The holiday DATES now live once in shared/nse_holidays.json, bundled here
 * and read by the Python side at import.
 */

import calendar from "../../shared/nse_holidays.json";

export const NSE_TRADING_HOLIDAYS: string[] = calendar.holidays;

// The session window, mirroring aggregator/tick_guard.py's MARKET_OPEN/MARKET_CLOSE
// (the backend's canonical definition) — keep the two in sync.
// 2026-08-03: NSE extended F&O trading to 15:40 IST (was 15:30).
export const SESSION_START_MIN = 9 * 60 + 15; // 09:15 IST
export const SESSION_END_MIN = 15 * 60 + 40; // 15:40 IST

/**
 * Current IST wall-clock as a Date whose LOCAL getters (getHours/getDate/…)
 * read IST values regardless of the host timezone — the toLocaleString
 * re-parse trick both pages already used. Never call toISOString() on the
 * result (it re-converts through UTC and can land on the wrong day).
 */
export function nowIST(): Date {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
}

/** YYYY-MM-DD via LOCAL getters — pairs with nowIST()/`new Date(s+'T00:00:00')`. */
export function dateStrFromLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "Not a trading session": weekend OR published holiday (YYYY-MM-DD in). */
export function isTradingHoliday(dateStr: string): boolean {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  if (day === 0 || day === 6) return true;
  return NSE_TRADING_HOLIDAYS.includes(dateStr);
}

/**
 * Walks backward to the previous actual trading session — "the day before
 * expiry" is not always literally yesterday once weekends/holidays land.
 */
export function previousTradingDay(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  let candidate: string;
  do {
    d.setDate(d.getDate() - 1);
    candidate = dateStrFromLocalDate(d);
  } while (isTradingHoliday(candidate));
  return candidate;
}

/**
 * Is the market open right now (or at `at`, for tests)? Weekday, within
 * the session window (09:15–15:40 IST), and — unlike the two pages' old copies — NOT a published
 * holiday: the old versions only checked the weekday, so feed-staleness
 * watchdogs warned about "quiet feeds" on holidays the market never opened.
 * (The backend gained the same fix as tick_guard.is_market_open.)
 */
export function isMarketHoursNow(at?: Date): boolean {
  const ist = at ?? nowIST();
  const day = ist.getDay();
  if (day === 0 || day === 6) return false;
  if (NSE_TRADING_HOLIDAYS.includes(dateStrFromLocalDate(ist))) return false;
  const minutes = ist.getHours() * 60 + ist.getMinutes();
  return minutes >= SESSION_START_MIN && minutes <= SESSION_END_MIN;
}
